import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { db, COLLECTIONS } from '../firebase/config';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { 
  Moon, 
  CheckCircle, 
  XCircle, 
  Lightbulb, 
  ArrowRight, 
  Calendar,
  AlertTriangle,
  Save,
  Sun,
  TrendingUp,
  Target,
  Heart,
  Star,
  CalendarDays,
  CalendarRange,
  CalendarClock,
  BarChart,
  Award,
  RefreshCw,
  Users
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// קומפוננטה להצגת סיכום סיכום המחזור
const PeriodSummarySummaryView = ({ periodSummary, periodOpening, onBackToEdit, onChangePeriod }) => {  const getRatingEmoji = (rating) => {
    if (rating >= 4) return '😊';
    if (rating >= 3) return '😐';
    return '😞';
  };

  const getRatingText = (rating) => {
    if (rating >= 4) return 'מעולה!';
    if (rating >= 3) return 'סביר';
    return 'מאתגר';
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return 'text-green-600 bg-green-100';
    if (rating >= 3) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getPeriodTitle = (type) => {
    switch(type) {
      case 'day': return 'יום';
      case 'week': return 'שבוע';
      case 'month': return 'חודש';
      case 'quarter': return 'רבעון';
      case 'year': return 'שנה';
      default: return 'תקופה';
    }
  };

  const getPeriodIcon = (type) => {
    switch(type) {
      case 'day': return <Moon className="w-12 h-12" />;
      case 'week': return <CalendarDays className="w-12 h-12" />;
      case 'month': return <Calendar className="w-12 h-12" />;
      case 'quarter': return <CalendarRange className="w-12 h-12" />;
      case 'year': return <CalendarClock className="w-12 h-12" />;
      default: return <Calendar className="w-12 h-12" />;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* כותרת */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 text-white mb-8">
        <div className="flex items-center gap-4">
          {getPeriodIcon(periodSummary.periodType)}
          <div>
            <h2 className="text-3xl font-bold mb-2">
              סיכום ה{getPeriodTitle(periodSummary.periodType)} שלך
            </h2>
            <p className="text-blue-100">
              {new Date().toLocaleDateString('he-IL', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
        </div>
      </div>

      {/* דירוג כללי */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="text-center">
          <div className="text-6xl mb-4">{getRatingEmoji(periodSummary.overallRating)}</div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            {getPeriodTitle(periodSummary.periodType)} {getRatingText(periodSummary.overallRating)}
          </h3>
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-lg font-medium ${getRatingColor(periodSummary.overallRating)}`}>
            <Star className="w-5 h-5" />
            {periodSummary.overallRating}/5
          </div>
        </div>
      </div>

      {/* סטטיסטיקות */}
      {periodSummary.periodType === 'day' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <div className="text-3xl font-bold text-green-600 mb-1">
              {periodSummary.completedTasks?.length || 0}
            </div>
            <p className="text-gray-600">משימות הושלמו</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
            <XCircle className="w-12 h-12 text-red-600 mx-auto mb-3" />
            <div className="text-3xl font-bold text-red-600 mb-1">
              {periodSummary.incompleteTasks?.length || 0}
            </div>
            <p className="text-gray-600">משימות לא הושלמו</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
            <Target className="w-12 h-12 text-blue-600 mx-auto mb-3" />
            <div className="text-3xl font-bold text-blue-600 mb-1">
              {periodOpening?.selectedTasks?.length || periodOpening?.goals?.length || 0}
            </div>
            <p className="text-gray-600">יעדים תוכננו</p>
          </div>
        </div>
      )}

      {/* סטטיסטיקות למחזורים ארוכים */}
      {periodSummary.periodType !== 'day' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
            <Award className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <div className="text-3xl font-bold text-green-600 mb-1">
              {periodSummary.goalsAchieved || 0}
            </div>
            <p className="text-gray-600">יעדים הושגו</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
            <TrendingUp className="w-12 h-12 text-blue-600 mx-auto mb-3" />
            <div className="text-3xl font-bold text-blue-600 mb-1">
              {periodSummary.progressPercentage || 0}%
            </div>
            <p className="text-gray-600">התקדמות</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
            <RefreshCw className="w-12 h-12 text-purple-600 mx-auto mb-3" />
            <div className="text-3xl font-bold text-purple-600 mb-1">
              {periodSummary.lessonsLearned || 0}
            </div>
            <p className="text-gray-600">תובנות</p>
          </div>
        </div>
      )}

      {/* תוכן הסיכום */}
      <div className="space-y-6">
        
        {/* הישגים */}
        {periodSummary.achievements && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              הישגי ה{getPeriodTitle(periodSummary.periodType)}
            </h3>
            <p className="text-gray-700 bg-green-50 p-4 rounded-lg">
              {periodSummary.achievements}
            </p>
          </div>
        )}

        {/* אתגרים */}
        {periodSummary.challenges && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              אתגרים וחסמים
            </h3>
            <p className="text-gray-700 bg-red-50 p-4 rounded-lg">
              {periodSummary.challenges}
            </p>
          </div>
        )}

        {/* תובנות */}
        {periodSummary.insights && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-600" />
              תובנות מה{getPeriodTitle(periodSummary.periodType)}
            </h3>
            <p className="text-gray-700 bg-yellow-50 p-4 rounded-lg">
              {periodSummary.insights}
            </p>
          </div>
        )}

        {/* תכנון לתקופה הבאה */}
        {periodSummary.nextPeriodPlan && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-blue-600" />
              התכנון ל{getPeriodTitle(periodSummary.periodType)} הבא
            </h3>
            <p className="text-gray-700 bg-blue-50 p-4 rounded-lg">
              {periodSummary.nextPeriodPlan}
            </p>
          </div>
        )}

        {/* לקחים שנלמדו - למחזורים ארוכים */}
        {periodSummary.periodType !== 'day' && periodSummary.lessonsDetails && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-purple-600" />
              לקחים שנלמדו
            </h3>
            <p className="text-gray-700 bg-purple-50 p-4 rounded-lg">
              {periodSummary.lessonsDetails}
            </p>
          </div>
        )}

        {/* כפתורי פעולה */}
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
          <div className="text-center mb-4">
            <p className="text-lg text-gray-700">
              כל הכבוד על סיום ה{getPeriodTitle(periodSummary.periodType)}! 🌟
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {getPeriodTitle(periodSummary.periodType)} הבא מלא באפשרויות
            </p>
          </div>
          
          <div className="flex gap-4 justify-center">
            <button
              onClick={onBackToEdit}
              className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
            >
              ערוך סיכום {getPeriodTitle(periodSummary.periodType)}
            </button>
          </div>
          {/* כפתורי מעבר מהיר לתקופות אחרות */}
<div className="mt-6 pt-6 border-t border-gray-200">
  <p className="text-sm text-gray-600 mb-3 text-center">רוצה למלא סיכום לתקופה נוספת?</p>
  <div className="flex gap-2 justify-center flex-wrap">
    {['day', 'week', 'month', 'quarter', 'year'].filter(type => type !== periodOpening.periodType).map(type => (
      <button
        key={type}
        onClick={() => onChangePeriod(type)}
        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
      >
        {type === 'day' && <Sun className="w-4 h-4" />}
        {type === 'week' && <CalendarDays className="w-4 h-4" />}
        {type === 'month' && <Calendar className="w-4 h-4" />}
        {type === 'quarter' && <CalendarRange className="w-4 h-4" />}
        {type === 'year' && <CalendarClock className="w-4 h-4" />}
סיכום {getPeriodTitle(type)}   
   </button>
    ))}
  </div>
</div>
        </div>
      </div>
    </div>
  );
};

const PeriodSummary = () => {
  const { currentUser } = useAuth();
  const { tasks } = useData();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [hasSummarizedToday, setHasSummarizedToday] = useState(false);
  const [todaySummary, setTodaySummary] = useState(null);
  const [todayOpening, setTodayOpening] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [showSummaryView, setShowSummaryView] = useState(false);
  const [periodType, setPeriodType] = useState('day'); // day, week, month, quarter, year
  
  const [formData, setFormData] = useState({
    completedTasks: [],
    incompleteTasks: [],
    incompleteReasons: {},
    insights: '',
    nextPeriodPlan: '',
    overallRating: 3,
    challenges: '',
    achievements: '',
    goalsAchieved: 0,
    progressPercentage: 75,
    lessonsLearned: 0,
    lessonsDetails: '',
    keyAccomplishments: [],
    keyFailures: [],
    teamPerformance: '',
    processImprovements: ''
  });

  // בדיקה אם כבר סיכם תקופה והאם יש פתיחת תקופה
  useEffect(() => {
    checkPeriodData();
  }, [currentUser, periodType]);

  const checkPeriodData = async () => {
    if (!currentUser) return;
    
    try {
      const today = new Date();
      const todayString = today.toDateString();
      
      const userId = currentUser.uid || currentUser.id;
      
      // בדיקת פתיחת תקופה
      if (periodType === 'day') {
        // לפתיחות יום - בדוק גם ב-DAY_OPENINGS
        const dayOpeningQuery = query(
          collection(db, COLLECTIONS.DAY_OPENINGS),
          where('userId', '==', userId)
        );
        
        const dayOpeningSnapshot = await getDocs(dayOpeningQuery);
        if (!dayOpeningSnapshot.empty) {
          for (const doc of dayOpeningSnapshot.docs) {
            const docData = doc.data();
            let docDate = null;
            
            if (docData.date) {
              if (docData.date.toDate) {
                docDate = docData.date.toDate();
              } else if (docData.date instanceof Date) {
                docDate = docData.date;
              } else if (typeof docData.date === 'string') {
                docDate = new Date(docData.date);
              }
            }
            
            if (docDate && docDate.toDateString() === todayString) {
              setTodayOpening({ id: doc.id, ...docData });
              break;
            }
          }
        }
      } else {
        // לתקופות אחרות - חפש בקולקציה החדשה
        const openingQuery = query(
          collection(db, COLLECTIONS.PERIOD_OPENINGS || COLLECTIONS.DAY_OPENINGS),
          where('userId', '==', userId),
          where('periodType', '==', periodType)
        );
        
        const openingSnapshot = await getDocs(openingQuery);
        if (!openingSnapshot.empty) {
          for (const doc of openingSnapshot.docs) {
            const docData = doc.data();
            let docDate = null;
            
            if (docData.date) {
              if (docData.date.toDate) {
                docDate = docData.date.toDate();
              } else if (docData.date instanceof Date) {
                docDate = docData.date;
              } else if (typeof docData.date === 'string') {
                docDate = new Date(docData.date);
              }
            }
            
            if (isPeriodCurrent(docDate, periodType)) {
              setTodayOpening({ id: doc.id, ...docData });
              break;
            }
          }
        }
      }
      
      // בדיקת סיכום תקופה
      if (periodType === 'day') {
        // לסיכומי יום - בדוק גם ב-DAY_SUMMARIES
        const daySummaryQuery = query(
          collection(db, COLLECTIONS.DAY_SUMMARIES),
          where('userId', '==', userId)
        );
        
        const daySummarySnapshot = await getDocs(daySummaryQuery);
        if (!daySummarySnapshot.empty) {
          for (const doc of daySummarySnapshot.docs) {
            const docData = doc.data();
            let docDate = null;
            
            if (docData.date) {
              if (docData.date.toDate) {
                docDate = docData.date.toDate();
              } else if (docData.date instanceof Date) {
                docDate = docData.date;
              } else if (typeof docData.date === 'string') {
                docDate = new Date(docData.date);
              }
            }
            
            if (docDate && docDate.toDateString() === todayString) {
              setHasSummarizedToday(true);
              setTodaySummary({ id: doc.id, ...docData });
              setFormData({
                completedTasks: docData.completedTasks || [],
                incompleteTasks: docData.incompleteTasks || [],
                incompleteReasons: docData.incompleteReasons || {},
                insights: docData.insights || '',
                nextPeriodPlan: docData.nextPeriodPlan || docData.tomorrowTasks || '',
                overallRating: docData.overallRating || 3,
                challenges: docData.challenges || '',
                achievements: docData.achievements || '',
                goalsAchieved: docData.goalsAchieved || 0,
                progressPercentage: docData.progressPercentage || 75,
                lessonsLearned: docData.lessonsLearned || 0,
                lessonsDetails: docData.lessonsDetails || '',
                keyAccomplishments: docData.keyAccomplishments || [],
                keyFailures: docData.keyFailures || [],
                teamPerformance: docData.teamPerformance || '',
                processImprovements: docData.processImprovements || ''
              });
              break;
            }
          }
        }
      } else {
        // לתקופות אחרות - חפש בקולקציה החדשה
        const summaryQuery = query(
          collection(db, COLLECTIONS.PERIOD_SUMMARIES || COLLECTIONS.DAY_SUMMARIES),
          where('userId', '==', userId),
          where('periodType', '==', periodType)
        );
        
        const summarySnapshot = await getDocs(summaryQuery);
        if (!summarySnapshot.empty) {
          for (const doc of summarySnapshot.docs) {
            const docData = doc.data();
            let docDate = null;
            
            if (docData.date) {
              if (docData.date.toDate) {
                docDate = docData.date.toDate();
              } else if (docData.date instanceof Date) {
                docDate = docData.date;
              } else if (typeof docData.date === 'string') {
                docDate = new Date(docData.date);
              }
            }
            
            if (isPeriodCurrent(docDate, periodType)) {
              setHasSummarizedToday(true);
              setTodaySummary({ id: doc.id, ...docData });
              setFormData({
                completedTasks: docData.completedTasks || [],
                incompleteTasks: docData.incompleteTasks || [],
                incompleteReasons: docData.incompleteReasons || {},
                insights: docData.insights || '',
                nextPeriodPlan: docData.nextPeriodPlan || docData.tomorrowTasks || '',
                overallRating: docData.overallRating || 3,
                challenges: docData.challenges || '',
                achievements: docData.achievements || '',
                goalsAchieved: docData.goalsAchieved || 0,
                progressPercentage: docData.progressPercentage || 75,
                lessonsLearned: docData.lessonsLearned || 0,
                lessonsDetails: docData.lessonsDetails || '',
                keyAccomplishments: docData.keyAccomplishments || [],
                keyFailures: docData.keyFailures || [],
                teamPerformance: docData.teamPerformance || '',
                processImprovements: docData.processImprovements || ''
              });
              break;
            }
          }
        }
      }
    } catch (error) {
      console.error('Error checking period data:', error);
    } finally {
      setCheckingExisting(false);
    }
  };

  // פונקציה לבדיקה אם התקופה היא הנוכחית
  const isPeriodCurrent = (date, type) => {
    const now = new Date();
    const nowString = now.toDateString();
    
    switch(type) {
      case 'day':
        return date && date.toDateString() === nowString;
      case 'week':
        const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
        const weekEnd = new Date(now.setDate(now.getDate() - now.getDay() + 6));
        return date >= weekStart && date <= weekEnd;
      case 'month':
        return date && date.getMonth() === new Date().getMonth() && date.getFullYear() === new Date().getFullYear();
      case 'quarter':
        const currentQuarter = Math.floor(new Date().getMonth() / 3);
        const dateQuarter = Math.floor(date.getMonth() / 3);
        return dateQuarter === currentQuarter && date.getFullYear() === new Date().getFullYear();
      case 'year':
        return date && date.getFullYear() === new Date().getFullYear();
      default:
        return false;
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTaskToggle = (taskId, field) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(taskId)
        ? prev[field].filter(id => id !== taskId)
        : [...prev[field], taskId]
    }));
  };

  const handleIncompleteReason = (taskId, reason) => {
    setFormData(prev => ({
      ...prev,
      incompleteReasons: {
        ...prev.incompleteReasons,
        [taskId]: reason
      }
    }));
  };

  const handleAddToList = (field, value) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value]
      }));
    }
  };

  const handleRemoveFromList = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userId = currentUser.uid || currentUser.id;
      
const summaryData = {
  userId: userId,
  userName: currentUser?.name || currentUser?.displayName || currentUser?.email?.split('@')[0] || 'משתמש',
  userRole: currentUser?.role || 'employee',
  departmentId: currentUser?.departmentId || null,
  teamId: currentUser?.teamId || null,
  // ... כל השאר
        date: new Date(),
        periodType: periodType,
        openingId: todayOpening?.id || null,
        overallRating: formData.overallRating,
        insights: formData.insights,
        challenges: formData.challenges,
        achievements: formData.achievements,
        nextPeriodPlan: formData.nextPeriodPlan,
        createdAt: serverTimestamp()
      };

      // הוסף שדות ספציפיים לפי סוג התקופה
      if (periodType === 'day') {
        summaryData.completedTasks = formData.completedTasks;
        summaryData.incompleteTasks = formData.incompleteTasks;
        summaryData.incompleteReasons = formData.incompleteReasons;
      }
      
      if (periodType !== 'day') {
        summaryData.goalsAchieved = formData.goalsAchieved;
        summaryData.progressPercentage = formData.progressPercentage;
        summaryData.lessonsLearned = formData.keyAccomplishments.length + formData.keyFailures.length;
        summaryData.lessonsDetails = formData.lessonsDetails;
        summaryData.keyAccomplishments = formData.keyAccomplishments;
        summaryData.keyFailures = formData.keyFailures;
      }
      
      if (periodType === 'quarter' || periodType === 'year') {
        summaryData.teamPerformance = formData.teamPerformance;
        summaryData.processImprovements = formData.processImprovements;
      }

      const collectionName = COLLECTIONS.PERIOD_SUMMARIES || COLLECTIONS.DAY_SUMMARIES;
      const docRef = await addDoc(collection(db, collectionName), summaryData);
      
      setTodaySummary({ id: docRef.id, ...summaryData });
      setHasSummarizedToday(true);
      setShowSummaryView(true);
      
      alert(`סיכום ${getPeriodTitle(periodType)} נשמר בהצלחה! 🌙`);
      
    } catch (error) {
      console.error('Error saving period summary:', error);
      alert('שגיאה בשמירת סיכום התקופה');
    } finally {
      setLoading(false);
    }
  };

  const getPeriodTitle = (type) => {
    switch(type) {
      case 'day': return 'יום';
      case 'week': return 'שבוע';
      case 'month': return 'חודש';
      case 'quarter': return 'רבעון';
      case 'year': return 'שנה';
      default: return 'תקופה';
    }
  };

  const getPeriodIcon = (type) => {
    switch(type) {
      case 'day': return <Moon className="w-8 h-8" />;
      case 'week': return <CalendarDays className="w-8 h-8" />;
      case 'month': return <Calendar className="w-8 h-8" />;
      case 'quarter': return <CalendarRange className="w-8 h-8" />;
      case 'year': return <CalendarClock className="w-8 h-8" />;
      default: return <Calendar className="w-8 h-8" />;
    }
  };

  // משימות רלוונטיות למשתמש
  const relevantTasks = tasks.filter(task => 
    task.assigneeId === currentUser?.id || task.createdBy === currentUser?.id
  );

  // משימות שנבחרו בפתיחת היום
  const selectedTasks = todayOpening?.selectedTasks 
    ? relevantTasks.filter(task => todayOpening.selectedTasks.includes(task.id))
    : [];

  if (checkingExisting) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // אם אין פתיחת תקופה
  if (!todayOpening) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-800 mb-2">
            לא ניתן לעשות סיכום {getPeriodTitle(periodType)}
          </h2>
          <p className="text-red-700 mb-6">
            כדי לסכם {getPeriodTitle(periodType)}, צריך קודם לפתוח את ה{getPeriodTitle(periodType)}!
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => navigate('/period-opening')}
              className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2 font-medium"
            >
              <Sun className="w-5 h-5" />
              פתח {getPeriodTitle(periodType)} עכשיו
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
            >
              חזור לדאשבורד
            </button>
          </div>
        </div>
      </div>
    );
  }

  // אם כבר סיכם התקופה ורוצה לראות את הסיכום
  if (hasSummarizedToday && showSummaryView) {
    return (
      <div className="p-6">
  <PeriodSummarySummaryView 
  periodSummary={todaySummary}
  periodOpening={todayOpening}
  onBackToEdit={() => setShowSummaryView(false)}
  onChangePeriod={(newPeriod) => {
    setPeriodType(newPeriod);
    setHasSummarizedToday(false);
    setCheckingExisting(true);
    setShowSummaryView(false);
  }}
/>
      </div>
    );
  }

  // טופס סיכום תקופה
  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
            {getPeriodIcon(periodType)}
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {hasSummarizedToday ? `סיכום ה${getPeriodTitle(periodType)} שלך` : `סיכום ${getPeriodTitle(periodType)}`}
            </h1>
            <p className="text-gray-600">
              {hasSummarizedToday 
                ? 'כבר סיכמת את התקופה - אתה יכול לעדכן את הפרטים'
                : `בואו נסכם את ה${getPeriodTitle(periodType)} ונתכנן ל${getPeriodTitle(periodType)} הבא! 🌟`
              }
            </p>
          </div>
        </div>
        
        {/* Period Type Selector */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            בחר סוג תקופה:
          </label>
          <div className="flex gap-2 flex-wrap">
            {['day', 'week', 'month', 'quarter', 'year'].map(type => (
              <button
                key={type}
                type="button"
                onClick={() => {
                  setPeriodType(type);
                  setHasSummarizedToday(false);
                  setCheckingExisting(true);
                  setShowSummaryView(false);
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  periodType === type
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {getPeriodTitle(type)}
              </button>
            ))}
          </div>
        </div>
        
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-purple-800">
            <Calendar className="w-5 h-5" />
            <span className="font-medium">
              {new Date().toLocaleDateString('he-IL', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </span>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        
        {/* יעדים מרכזיים שהושלמו - רק ליום */}
        {periodType === 'day' && selectedTasks.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-semibold">יעדים מרכזיים מהיום - מה הושלם?</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {selectedTasks.map(task => (
                <div key={task.id} className={`p-3 border rounded-lg ${
                  formData.completedTasks.includes(task.id)
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200'
                }`}>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.completedTasks.includes(task.id)}
                      onChange={() => handleTaskToggle(task.id, 'completedTasks')}
                      className="w-4 h-4 text-green-600"
                    />
                    <div className="flex-1">
                      <p className={`font-medium ${formData.completedTasks.includes(task.id) ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                        {task.title}
                      </p>
                      <p className="text-sm text-gray-500">
                        {task.priority} • {task.status}
                      </p>
                    </div>
                    {formData.completedTasks.includes(task.id) && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* יעדים שהושגו - למחזורים ארוכים */}
        {periodType !== 'day' && todayOpening?.goals && todayOpening.goals.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Award className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-semibold">
                יעדי ה{getPeriodTitle(periodType)} - מה הושג?
              </h2>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-3">
                {todayOpening.goals.map((goal, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium">{goal}</p>
                  </div>
                ))}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  כמה יעדים הושגו במלואם?
                </label>
                <input
                  type="number"
                  min="0"
                  max={todayOpening.goals.length}
                  value={formData.goalsAchieved}
                  onChange={(e) => handleInputChange('goalsAchieved', parseInt(e.target.value) || 0)}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
                <span className="text-sm text-gray-500 mr-2">
                  מתוך {todayOpening.goals.length}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* התקדמות כללית - למחזורים ארוכים */}
        {periodType !== 'day' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold">התקדמות כללית</h2>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                אחוז ההתקדמות ביעדי ה{getPeriodTitle(periodType)}
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={formData.progressPercentage}
                  onChange={(e) => handleInputChange('progressPercentage', e.target.value)}
                  className="flex-1"
                />
                <span className="text-2xl font-bold text-blue-600">
                  {formData.progressPercentage}%
                </span>
              </div>
            </div>
          </div>
        )}

        {/* משימות שלא הושלמו - רק ליום */}
        {periodType === 'day' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <XCircle className="w-6 h-6 text-red-600" />
              <h2 className="text-xl font-semibold">מה לא הושלם ולמה?</h2>
            </div>
            
            <div className="space-y-4">
              {selectedTasks.filter(task => !formData.completedTasks.includes(task.id)).map(task => (
                <div key={task.id} className="p-4 border border-red-200 rounded-lg bg-red-50">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="w-5 h-5 text-red-500" />
                    <p className="font-medium text-gray-900">{task.title}</p>
                  </div>
                  <textarea
                    value={formData.incompleteReasons[task.id] || ''}
                    onChange={(e) => handleIncompleteReason(task.id, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    rows="2"
                    placeholder="למה המשימה לא הושלמה? מה היו החסמים?"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* הישגים מרכזיים - למחזורים ארוכים */}
        {periodType !== 'day' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Award className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-semibold">הישגים מרכזיים</h2>
            </div>
            
            <div className="space-y-2">
              {formData.keyAccomplishments.map((accomplishment, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                  <span className="flex-1">{accomplishment}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFromList('keyAccomplishments', index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              ))}
              
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="הוסף הישג מרכזי..."
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddToList('keyAccomplishments', e.target.value);
                      e.target.value = '';
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* הישגים וחסמים */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-semibold">
                {periodType === 'day' ? 'הישגי היום' : `הישגי ה${getPeriodTitle(periodType)}`}
              </h2>
            </div>
            
            <textarea
              value={formData.achievements}
              onChange={(e) => handleInputChange('achievements', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              rows="4"
              placeholder={
                periodType === 'day' ? "מה הלך טוב היום? על מה אתה גאה?" :
                `מה היו ההישגים המרכזיים ב${getPeriodTitle(periodType)}?`
              }
            />
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-xl font-semibold">אתגרים וחסמים</h2>
            </div>
            
            <textarea
              value={formData.challenges}
              onChange={(e) => handleInputChange('challenges', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              rows="4"
              placeholder={
                periodType === 'day' ? "מה היה מאתגר? איך אפשר להתמודד עם זה בעתיד?" :
                `אילו אתגרים וחסמים היו ב${getPeriodTitle(periodType)}?`
              }
            />
          </div>
        </div>

        {/* תובנות מהתקופה */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Lightbulb className="w-6 h-6 text-yellow-600" />
            <h2 className="text-xl font-semibold">
              תובנות מה{getPeriodTitle(periodType)}
            </h2>
          </div>
          
          <textarea
            value={formData.insights}
            onChange={(e) => handleInputChange('insights', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
            rows="4"
            placeholder={
              periodType === 'day' ? "מה למדת היום? איזה תובנות יש לך? מה תעשה אחרת בפעם הבאה?" :
              `מה למדת ב${getPeriodTitle(periodType)} הזה? איזה תובנות רוצה לקחת קדימה?`
            }
            required
          />
        </div>

        {/* לקחים שנלמדו - למחזורים ארוכים */}
        {periodType !== 'day' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <RefreshCw className="w-6 h-6 text-purple-600" />
              <h2 className="text-xl font-semibold">לקחים שנלמדו</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-gray-700 mb-2">מה עבד פחות טוב?</h3>
                <div className="space-y-2">
                  {formData.keyFailures.map((failure, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-red-50 rounded-lg">
                      <span className="flex-1">{failure}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveFromList('keyFailures', index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                  
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="הוסף דבר שלא עבד טוב..."
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddToList('keyFailures', e.target.value);
                          e.target.value = '';
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-medium text-gray-700 mb-2">מה ניתן לשפר?</h3>
                <textarea
                  value={formData.lessonsDetails}
                  onChange={(e) => handleInputChange('lessonsDetails', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  rows="3"
                  placeholder="מה למדת? איך אפשר לשפר בתקופה הבאה?"
                />
              </div>
            </div>
          </div>
        )}

        {/* ביצועי צוות - לרבעון ושנה */}
        {(periodType === 'quarter' || periodType === 'year') && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-indigo-600" />
              <h2 className="text-xl font-semibold">ביצועי צוות</h2>
            </div>
            
            <textarea
              value={formData.teamPerformance}
              onChange={(e) => handleInputChange('teamPerformance', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              rows="4"
              placeholder="איך הצוות עבד? מה היה טוב ומה צריך לשפר?"
            />
          </div>
        )}

        {/* תכנון לתקופה הבאה */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <ArrowRight className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold">
              תכנון ל{getPeriodTitle(periodType)} הבא
            </h2>
          </div>
          
          <textarea
            value={formData.nextPeriodPlan}
            onChange={(e) => handleInputChange('nextPeriodPlan', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            rows="4"
            placeholder={
              periodType === 'day' ? "מה מתוכנן למחר? איזה משימות בעדיפות? מה הלקח מהיום?" :
              `מה היעדים ל${getPeriodTitle(periodType)} הבא? מה חשוב להתמקד בו?`
            }
          />
        </div>

        {/* הערכה כללית */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            {getPeriodIcon(periodType)}
            <h2 className="text-xl font-semibold">
              הערכה כללית של ה{getPeriodTitle(periodType)}
            </h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                איך היה ה{getPeriodTitle(periodType)}? (1-5)
              </label>
              <div className="flex items-center gap-4">
                {[1, 2, 3, 4, 5].map(level => (
                  <button
                    key={level}
                    type="button"
                    onClick={() => handleInputChange('overallRating', level)}
                    className={`w-12 h-12 rounded-full border-2 flex items-center justify-center font-bold transition-all ${
                      formData.overallRating === level
                        ? 'border-purple-500 bg-purple-100 text-purple-700'
                        : 'border-gray-300 text-gray-500 hover:border-purple-300'
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>😞 גרוע</span>
                <span>😐 בינוני</span>
                <span>😊 מעולה</span>
              </div>
            </div>
          </div>
        </div>

        {/* כפתור שמירה */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
          >
            ביטול
          </button>
          
          <button
            type="submit"
            disabled={loading}
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <Save className="w-5 h-5" />
                {hasSummarizedToday ? `עדכן סיכום ${getPeriodTitle(periodType)}` : `שמור סיכום ${getPeriodTitle(periodType)}`}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PeriodSummary;